import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Package, Eye, Type, List, ToggleLeft, Calendar, Hash, FileText,
  Clock, Image as ImageIcon, Upload
} from 'lucide-react';

const INPUT_TYPE_MAP = {
  dropdown: { icon: List, label: 'Dropdown' },
  select: { icon: List, label: 'Dropdown' },
  radio: { icon: ToggleLeft, label: 'Radio Buttons' },
  checkbox: { icon: ToggleLeft, label: 'Checkboxes' },
  text: { icon: Type, label: 'Text Input' },
  textarea: { icon: FileText, label: 'Text Area' },
  number: { icon: Hash, label: 'Number Input' },
  date: { icon: Calendar, label: 'Date Picker' },
  time: { icon: Clock, label: 'Time Picker' },
  'button-select': { icon: ToggleLeft, label: 'Button Select' },
  'image-cards': { icon: ImageIcon, label: 'Image Cards' },
  gallery: { icon: ImageIcon, label: 'Image Gallery' },
  file_upload: { icon: Upload, label: 'File Upload' },
};

const getInputTypeInfo = (type) => INPUT_TYPE_MAP[type] || { icon: List, label: 'Dropdown' };

/**
 * Live preview panel for CustomOptionsModal.
 * Uses native HTML form elements to avoid Radix compose-refs infinite loop on mobile.
 */
const LivePreviewPanel = ({ localConfig, formData, calculatePreviewTotal }) => (
  <div className="w-full bg-slate-50 overflow-y-auto">
    <div className="sticky top-0 z-10 bg-slate-100 border-b px-6 py-3">
      <div className="flex items-center gap-2">
        <Eye className="h-5 w-5 text-slate-600" />
        <span className="font-semibold text-slate-800">Live Preview</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">How customers will see this form</p>
    </div>

    <div className="p-4 md:p-6">
      <Card className="bg-white shadow-md border-0">
        <div className="p-4 border-b bg-gradient-to-r from-slate-50 to-white">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
              <Package className="h-8 w-8 text-slate-400" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{formData.title || 'Your Product'}</h3>
              <p className="text-lg font-bold text-blue-600">₹{parseFloat(formData.price || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <CardContent className="p-4 space-y-6">
          {localConfig.options?.length > 0 ? (
            <>
              {localConfig.options.map((option, optIdx) => (
                <OptionPreviewBlock key={option.id || optIdx} option={option} />
              ))}
              <PriceBreakdown formData={formData} calculatePreviewTotal={calculatePreviewTotal} />
            </>
          ) : (
            <EmptyPreview />
          )}
        </CardContent>
      </Card>

      {localConfig.options?.length > 0 && (
        <Button className="w-full mt-4 h-12 text-lg font-semibold" disabled>
          Add to Cart - ₹{calculatePreviewTotal().total.toLocaleString()}
        </Button>
      )}
    </div>
  </div>
);

/* ─── Option preview block ──────────────────────────────────────────── */

const OptionPreviewBlock = ({ option }) => {
  const typeInfo = getInputTypeInfo(option.type);
  const InputIcon = typeInfo.icon;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Label className="text-base font-semibold text-slate-800">{option.name || 'Option Name'}</Label>
          {option.required && <span className="text-red-500 text-sm">*</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
            <InputIcon className="h-3 w-3 mr-1" />{typeInfo.label}
          </Badge>
          {option.mechanism && (
            <Badge variant="outline" className={`text-xs ${option.mechanism === 'addon' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
              {option.mechanism === 'addon' ? 'Add-on (Multi-select)' : 'Variant (Single choice)'}
            </Badge>
          )}
        </div>
      </div>
      {option.helpText && <p className="text-sm text-slate-500">{option.helpText}</p>}
      <ChoiceRenderer option={option} />
    </div>
  );
};

/* ─── Choice renderer — uses native HTML elements to avoid Radix ref bugs ── */

const ChoiceRenderer = ({ option }) => {
  const choices = option.choices || [];
  switch (option.type) {
    case 'dropdown':
    case 'select':
    case undefined:
      // Type determines UI: dropdown/select always shows a <select>, regardless of mechanism
      return choices.length > 0 ? (
        <select className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm bg-white focus:border-blue-400 focus:outline-none transition-colors">
          <option value="">{`Select ${option.name || 'option'}...`}</option>
          {choices.map((c, i) => (
            <option key={c.value || `c-${i}`} value={c.value || c.label || `c-${i}`}>
              {c.label || c.name || 'Unnamed'}
              {parseFloat(c.price) > 0 ? ` (+₹${parseFloat(c.price).toLocaleString()})` : ''}
            </option>
          ))}
        </select>
      ) : null;

    case 'radio':
      return choices.length > 0 ? (
        <div className="space-y-2">
          {choices.map((c, i) => (
            <label key={c.value || i} className="flex items-center justify-between p-3 border-2 rounded-xl hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer">
              <div className="flex items-center gap-3">
                <input type="radio" name={`preview-${option.id}`} value={c.value || String(i)} className="h-5 w-5 text-blue-600 border-gray-300" />
                <span className="font-medium text-slate-700">{c.label || c.name || 'Unnamed'}</span>
              </div>
              <PriceBadge price={c.price} />
            </label>
          ))}
        </div>
      ) : null;

    case 'checkbox':
      return choices.length > 0 ? <NativeCheckboxList choices={choices} optionId={option.id} /> : null;

    case 'button-select':
      return choices.length > 0 ? (
        <div className="flex flex-wrap gap-3">
          {choices.map((c, i) => (
            <button key={c.value || i} type="button" className="relative px-5 py-3 rounded-lg font-medium bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-md transition-all">
              <div className="text-center">
                <div className="text-slate-700">{c.label || c.name || 'Unnamed'}</div>
                {parseFloat(c.price) > 0 && <div className="text-sm font-semibold text-green-600 mt-1">+₹{parseFloat(c.price).toLocaleString()}</div>}
              </div>
            </button>
          ))}
        </div>
      ) : null;

    case 'image-cards':
      return choices.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {choices.map((c, i) => (
            <Card key={c.value || i} className="cursor-pointer overflow-hidden hover:shadow-lg hover:ring-2 hover:ring-blue-400 transition-all">
              <div className="aspect-square relative bg-gray-100">
                {c.image_url ? <img src={c.image_url} alt={c.label} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-12 w-12 text-gray-400" /></div>}
              </div>
              <div className="p-2 text-center">
                <p className="font-medium text-sm">{c.label || c.name || 'Unnamed'}</p>
                {parseFloat(c.price) > 0 && <p className="text-green-600 font-semibold text-sm mt-1">+₹{parseFloat(c.price).toLocaleString()}</p>}
              </div>
            </Card>
          ))}
        </div>
      ) : null;

    case 'gallery':
      return choices.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {choices.map((c, i) => (
            <div key={c.value || i} className="relative cursor-pointer rounded-lg overflow-hidden ring-2 ring-gray-200 hover:ring-blue-400 hover:shadow-md transition-all">
              <div className="aspect-square relative bg-gray-100">
                {c.image_url ? <img src={c.image_url} alt={c.label} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-8 w-8 text-gray-400" /></div>}
              </div>
              {c.label && <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 text-center">{c.label}</div>}
            </div>
          ))}
        </div>
      ) : null;

    case 'text':
      return <input type="text" placeholder={option.placeholder || `Enter ${option.name || 'value'}...`} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-400 focus:outline-none" disabled />;
    case 'textarea':
      return <textarea placeholder={option.placeholder || `Enter ${option.name || 'details'}...`} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm resize-none focus:border-blue-400 focus:outline-none" rows={3} disabled />;
    case 'number':
      return <input type="number" placeholder={option.placeholder || '0'} className="max-w-[150px] px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-400 focus:outline-none" disabled />;
    case 'date':
      return <input type="date" className="max-w-[200px] px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-400 focus:outline-none" disabled />;
    case 'time':
      return (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-500" />
          <input type="time" className="max-w-[150px] px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-blue-400 focus:outline-none" disabled />
        </div>
      );
    case 'file_upload':
      return (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
          <Upload className="h-10 w-10 mx-auto text-slate-400 mb-2" />
          <p className="text-sm font-medium text-slate-600">Click to upload or drag and drop</p>
          <p className="text-xs text-slate-400 mt-1">
            {option.allowed_types_label || 'Images & Documents'}
            {option.max_size && ` (max ${option.max_size}MB)`}
          </p>
        </div>
      );
    default:
      return null;
  }
};

/* ─── Shared tiny components ────────────────────────────────────────── */

const NativeCheckboxList = ({ choices, optionId }) => (
  <div className="space-y-2">
    {choices.map((c, i) => (
      <label key={c.value || i} className="flex items-center justify-between p-3 border-2 rounded-xl hover:border-green-400 hover:bg-green-50/30 transition-all cursor-pointer">
        <div className="flex items-center gap-3">
          <input type="checkbox" className="h-5 w-5 rounded border-gray-300 text-green-600" />
          <span className="font-medium text-slate-700">{c.label || c.name || 'Unnamed'}</span>
        </div>
        {parseFloat(c.price) > 0 ? (
          <span className="font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-lg">+₹{parseFloat(c.price).toLocaleString()}</span>
        ) : (
          <span className="text-xs text-slate-400">Free</span>
        )}
      </label>
    ))}
  </div>
);

const PriceBadge = ({ price }) =>
  parseFloat(price) > 0 ? (
    <span className="font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-lg">+₹{parseFloat(price).toLocaleString()}</span>
  ) : (
    <span className="text-xs text-slate-400">Included</span>
  );

const PriceBreakdown = ({ formData, calculatePreviewTotal }) => (
  <div className="pt-5 border-t mt-5 space-y-3 bg-slate-50 -mx-4 px-4 py-4 rounded-b-lg">
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">Base Price</span>
      <span className="font-medium">₹{parseFloat(formData.price || 0).toLocaleString()}</span>
    </div>
    {calculatePreviewTotal().addOnsTotal > 0 && (
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">Selected Add-ons</span>
        <span className="text-green-600 font-medium">+₹{calculatePreviewTotal().addOnsTotal.toLocaleString()}</span>
      </div>
    )}
    <div className="flex justify-between pt-3 border-t border-slate-200">
      <span className="font-bold text-slate-800">Total</span>
      <span className="font-bold text-lg text-blue-600">₹{calculatePreviewTotal().total.toLocaleString()}</span>
    </div>
  </div>
);

const EmptyPreview = () => (
  <div className="text-center py-12 text-slate-400">
    <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
      <Package className="h-8 w-8 opacity-50" />
    </div>
    <p className="font-medium mb-1">No options configured</p>
    <p className="text-sm">Add options in the editor to see how they&apos;ll appear to customers</p>
  </div>
);

export default LivePreviewPanel;
